import { morphSection } from '@theme/section-renderer';

/**
 * Eagerly fetches and renders the cart drawer's "you might also like" strip.
 *
 * Unlike the theme's `<product-recommendations>` component, this loads immediately
 * on connect rather than waiting for an IntersectionObserver — it lives inside the
 * cart drawer's own scrollable item list, where it can start below the fold and
 * would otherwise never be reported as intersecting.
 */
class CartRecommendations extends HTMLElement {
  /** @type {Record<string, string>} */
  #cache = {};

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error' || mutation.attributeName === 'class') continue;
      if (mutation.attributeName === 'data-recommendations-performed' && this.dataset.recommendationsPerformed === 'true') {
        continue;
      }
      this.#load();
      break;
    }
  });

  connectedCallback() {
    this.#mutationObserver.observe(this, { attributes: true });
    if (this.dataset.recommendationsPerformed !== 'true') this.#load();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  #load() {
    const { productId, sectionId, intent, url } = this.dataset;
    if (!productId || !sectionId) return;

    const fetchUrl = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cached = this.#cache[fetchUrl];
    const result = cached ? Promise.resolve(cached) : fetch(fetchUrl).then((response) => {
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      return response.text();
    });

    result
      .then((html) => {
        this.#cache[fetchUrl] = html;

        if (html?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((error) => this.#handleError(error));
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error('Cart recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading cart recommendations';
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendations);
}
